import { inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import {
  ConfirmationActionModalComponent,
  ConfirmationActionModalData,
} from '@shared/components/modals/confirmation-action-modal/confirmation-action-modal.components';
import { BudgetType, GqlService } from '@shared/services/gql.service';
import { OverlayService } from '@shared/services/overlay.service';
import { MainQuery } from '@shared/store/main/main.query';

import { BudgetLibraryModel, BudgetLibraryStore } from './budget-library.store';

@Injectable({ providedIn: 'root' })
export class BudgetLibraryService {
  private budgetLibraryStore = inject(BudgetLibraryStore);
  private gqlService = inject(GqlService);
  private mainQuery = inject(MainQuery);
  private overlayService = inject(OverlayService);

  get() {
    return this.mainQuery.select('trialKey').pipe(
      switchMap(() => {
        this.budgetLibraryStore.setLoading(true);
        this.budgetLibraryStore.remove(() => true);

        return this.gqlService
          .listBudgetLibrary$([BudgetType.BUDGET_PRIMARY, BudgetType.BUDGET_SECONDARY], '')
          .pipe(
            map(({ data, errors, success }) => {
              const bLibrary = (data || []).map((val) => {
                return { ...val, id: val.budget_version_id };
              });

              this.budgetLibraryStore.set(bLibrary);

              this.budgetLibraryStore.setLoading(false);

              return { data, errors, success };
            })
          );
      })
    );
  }

  async removeBudgetVersion(item: BudgetLibraryModel) {
    const modalEvent = this.overlayService.openPopup<
      ConfirmationActionModalData,
      boolean,
      ConfirmationActionModalComponent
    >({
      data: {
        keywordToExecuteAction: 'Delete Budget',
        message:
          'This will permanently delete the selected budget version. This action cannot be undone and will remove all associated data.',
      },
      modal: ConfirmationActionModalComponent,
      settings: {
        header: 'Delete Budget Version?',
        primaryButton: {
          label: 'Delete Budget Version',
          variant: 'negative',
        },
      },
    }).afterClosed$;

    const resp = await firstValueFrom(modalEvent);

    if (resp?.data) {
      const { errors, success } = await firstValueFrom(
        this.gqlService.removeBudgetVersion$(item.budget_version_id)
      );

      if (success) {
        this.overlayService.success(`${item.budget_name} successfully removed!`);
        this.budgetLibraryStore.remove(item.budget_version_id);
      } else {
        this.overlayService.error(errors);
      }
    }
  }

  async updateBudgetVersionAsBaseline(id: string, organization_id: string) {
    const ref = this.overlayService.loading();
    const { data, errors, success } = await firstValueFrom(
      this.gqlService.setBudgetVersionAsBaseline$(id, organization_id)
    );

    if (errors.length) {
      this.overlayService.error(errors);
    } else if (success) {
      data?.forEach((x: BudgetLibraryModel) => {
        this.budgetLibraryStore.update(x.budget_version_id, x);
      });
      this.overlayService.success();
      ref.close();
    }
  }

  updateLastBudgetVersion(item: BudgetLibraryModel) {
    this.budgetLibraryStore.update(item?.budget_version_id, item as BudgetLibraryModel);
  }
}
